import {
  AiEnabledGuard,
  AiNotEnabledFallback,
  FeatureFlagGuard,
  PlanFeatureGuard,
  PlanGuard,
} from '@/components/guards';
import { useEffect, type ReactNode } from 'react';
import { useStore } from '@nanostores/react';
import { useSnackbars } from '@customerio/pluma-components/react';
import { Outlet, useRouter } from '@tanstack/react-router';
import NotFoundPage from '@/pages/not-found.page';
import { GeosetRequiredGuard } from '@/pages/workspaces/geofences/geoset-required-guard';
import { useAuth } from '@/services/auth';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { billing } from '@/services/billing';
import { VERIFICATION_REJECTED_MESSAGE } from '@/services/billing/messages';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { BILLING_PATHS } from '@/pages/settings/billing/billing-paths';
import Journeys from '@/pages/journeys';
import { AccountState, PlanFeature } from '@/services/auth/types';
import { BUILDER_PLAN_SLUG } from '@/services/auth/plan-slugs';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import type { Permission } from '@customerio/hydra-permissions';
import {
  AIBusinessProfile,
  AccountApiCredentialsPage,
  AccountApiKeysPage,
  AccountPartnerCreatePage,
  AccountPartnerPage,
  ServiceAccountDetailPage,
  AccountSettingsEditPage,
  AccountSettingsPage,
  AiSettingsPage,
  AssetsPage,
  AuditLogsPage,
  DeliverabilityPage,
  DesignStudioDashboardPage,
  DesignStudioGlobalStylesPage,
  EmailClientsPage,
  ExperimentalFeaturesPage,
  ExportsPage,
  GoogleIdentitiesPage,
  LayoutsPage,
  HarSanitizerPage,
  LayoutDetailPage,
  LayoutEditPage,
  LayoutStartersPage,
  MessageLibraryPage,
  NewLayoutPage,
  NewRolePage,
  NoAccessibleWorkspacesPage,
  AcceptGuestInvitationPage,
  PlansAndBillingPage,
  YourPlanPage,
  LlmUsagePage,
  ProfileGrowthPage,
  BillingHistoryPage,
  AvailablePlansPage,
  PaymentMethodsPage,
  PostCancelPage,
  PrivacySettingsPage,
  RolePage,
  RolesPage,
  TeamMembersPage,
  TeamMemberAddPage,
  TeamMemberEditPage,
  TeamMemberInviteGuestPage,
  SnippetsLayout,
  WorkspaceApiCredentialsPage,
  WorkspaceApiKeysPage,
  WorkspaceAutoArchivePage,
  WorkspaceConfirmDeletePage,
  WorkspaceEditPage,
  WorkspaceGeofencesSettingsPage,
  WorkspaceAuditLogsPage,
  WorkspaceLanguageSettingsPage,
  WorkspaceMergeOptionsPage,
  WorkspaceNewPage,
  WorkspaceSuppressionsPage,
  WorkspacesSettingsPage,
  WorkspaceCustomSmsSettingsPage,
  WorkspaceCustomSmsCredentialDetailPage,
} from './-route-pages';
import { DesignStudioRoute, LastWorkspaceResolver, LazyPage } from './-route-layouts';

export function AccountApiKeysRoute() {
  return (
    <ProtectedRoute permission="account/api_credentials/view" redirectTo="/settings">
      <LazyPage>
        <AccountApiKeysPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function AccountApiCredentialsRoute() {
  return (
    <ProtectedRoute permission="account/api_credentials/view" redirectTo="/settings">
      <LazyPage>
        <AccountApiCredentialsPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function WorkspaceApiCredentialsRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="account/api_credentials/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
            .href
        }
      >
        <LazyPage>
          <WorkspaceApiCredentialsPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function AccountServiceAccountRoute() {
  return (
    <ProtectedRoute permission="account/api_credentials/view" redirectTo="/settings">
      <LazyPage>
        <ServiceAccountDetailPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function WorkspaceServiceAccountRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="account/api_credentials/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
            .href
        }
      >
        <LazyPage>
          <ServiceAccountDetailPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function AccountAuditLogsRoute() {
  return (
    <ProtectedRoute permission="account/view" redirectTo="/settings" allowReadOnlyMasqAccess={true}>
      <LazyPage>
        <AuditLogsPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function ExperimentalFeaturesRoute() {
  return (
    <LazyPage>
      <ExperimentalFeaturesPage />
    </LazyPage>
  );
}

// Plans & Billing shell (SPE-3782). Layout route for `/settings/billing` and its tabs.
export function BillingRoute() {
  const isMigrationEnabled = useFeatureFlag(AccountFeatureFlag.HydraBilling);

  // Flag off: the embedded Ember app serves the billing URLs. Route ownership
  // (`route-ownership.ts`) falls back to `embedded` on the same flag, so the ownership answer and
  // the rendered app agree, and ownership of every billing tab flips atomically (SPE-3782).
  if (!isMigrationEnabled) {
    return <Journeys />;
  }

  // Every Ember billing tab route guards on `account/billing/view`
  // (`redirectToSettingsIfNoPermission` → `/settings`), so gate the shell layout on the same
  // permission — no child tab is reachable without it. The Ember guard is a plain permission
  // check with no read-only-masquerade exception, so this uses ProtectedRoute's default masq
  // handling (a masquerader who holds the permission is still admitted).
  return (
    <ProtectedRoute permission="account/billing/view" redirectTo="/settings">
      <LazyPage>
        <PlansAndBillingPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

// Your Plan tab. Renders inside the billing shell layout, which already gates on
// `account/billing/view`, so this adds no ProtectedRoute of its own.
export function YourPlanRoute() {
  const { account } = useAuth();
  const isOrdwayCustomer = useStore(billing.isOrdwayCustomer);
  const subscription = useStore(billing.subscription);
  const isVerificationRejected = useStore(billing.isVerificationRejected);
  const navigate = useSafeNavigate();

  // Ordway escape-hatch: an Ordway account that isn't in the `current` state and has no subscription
  // data has nothing to show here, so send it to available plans. Skip this when verification is
  // rejected — Available Plans redirects those accounts straight back to Your Plan, so redirecting
  // there would bounce the user between the two tabs forever.
  const shouldRedirect =
    isOrdwayCustomer &&
    account?.account_state !== AccountState.Current &&
    subscription == null &&
    !isVerificationRejected;

  useEffect(() => {
    if (shouldRedirect) {
      navigate(BILLING_PATHS.availablePlans);
    }
  }, [shouldRedirect, navigate]);

  if (shouldRedirect) {
    return null;
  }

  return (
    <LazyPage>
      <YourPlanPage />
    </LazyPage>
  );
}

// Credit usage tab. Renders inside the billing shell layout, which already gates on
// `account/billing/view`, so this adds no ProtectedRoute of its own.
export function LlmUsageRoute() {
  return (
    <LazyPage>
      <LlmUsagePage />
    </LazyPage>
  );
}

// Profile growth tab. Renders inside the billing shell layout, which already gates on
// `account/billing/view`. The tab and page are additionally gated on the `profile_growth` account
// flag: when it's off, this URL redirects to the Your Plan tab (matching the Ember route's
// `beforeModel` guard).
export function ProfileGrowthRoute() {
  const isProfileGrowthEnabled = useFeatureFlag(AccountFeatureFlag.ProfileGrowth);
  const navigate = useSafeNavigate();

  useEffect(() => {
    if (!isProfileGrowthEnabled) {
      navigate(BILLING_PATHS.yourPlan);
    }
  }, [isProfileGrowthEnabled, navigate]);

  if (!isProfileGrowthEnabled) {
    return null;
  }

  return (
    <LazyPage>
      <ProfileGrowthPage />
    </LazyPage>
  );
}

// Billing history tab. Renders inside the billing shell layout, which already gates on
// `account/billing/view`, so this adds no ProtectedRoute of its own.
export function BillingHistoryRoute() {
  return (
    <LazyPage>
      <BillingHistoryPage />
    </LazyPage>
  );
}

// Available plans (Journeys) tab. Renders inside the billing shell layout, which already gates on
// `account/billing/view`. An account whose verification has been rejected can't pick a plan here, so
// it's redirected to the Your Plan tab and shown the verification-rejected alert.
export function AvailablePlansRoute() {
  const isVerificationRejected = useStore(billing.isVerificationRejected);
  const navigate = useSafeNavigate();
  const snackbars = useSnackbars();

  useEffect(() => {
    if (isVerificationRejected) {
      snackbars.create({
        variant: 'error',
        id: 'billing.verification-rejected',
        message: VERIFICATION_REJECTED_MESSAGE,
      });
      navigate(BILLING_PATHS.yourPlan);
    }
  }, [isVerificationRejected, navigate, snackbars]);

  if (isVerificationRejected) {
    return null;
  }

  return (
    <LazyPage>
      <AvailablePlansPage />
    </LazyPage>
  );
}

// Payment Methods tab. Renders inside the billing shell layout (already gates on
// `account/billing/view`), so this adds no ProtectedRoute of its own.
export function PaymentMethodsRoute() {
  const isOrdwayCustomer = useStore(billing.isOrdwayCustomer);
  const isTrial = useStore(billing.isTrial);
  const isPendingCheckout = useStore(billing.isPendingCheckout);
  const isBuilderPlan = useStore(billing.isBuilderPlan);
  const navigate = useSafeNavigate();

  // An Ordway trial that hasn't started checkout and isn't on the Builder plan has no payment method
  // to manage yet, so send it to available plans (that page isn't migrated yet, so navigate by string
  // path).
  const shouldRedirect = isOrdwayCustomer && isTrial && !isPendingCheckout && !isBuilderPlan;

  useEffect(() => {
    if (shouldRedirect) {
      navigate(BILLING_PATHS.availablePlans);
    }
  }, [shouldRedirect, navigate]);

  if (shouldRedirect) {
    return null;
  }

  return (
    <LazyPage>
      <PaymentMethodsPage />
    </LazyPage>
  );
}

export function GoogleIdentitiesRoute() {
  return (
    <LazyPage>
      <GoogleIdentitiesPage />
    </LazyPage>
  );
}

type RolesGuardProps = { children: ReactNode };

function RolesGuard({ children }: RolesGuardProps) {
  return (
    <ProtectedRoute permission="account/team_members/view" redirectTo="/settings">
      <PlanFeatureGuard feature={PlanFeature.CustomRoles} redirectTo="/settings">
        {children}
      </PlanFeatureGuard>
    </ProtectedRoute>
  );
}

export function RolesRoute() {
  return (
    <RolesGuard>
      <LazyPage>
        <RolesPage />
      </LazyPage>
    </RolesGuard>
  );
}

export function TeamMembersRoute() {
  return (
    <ProtectedRoute permission="account/team_members/view" redirectTo="/settings">
      <LazyPage>
        <TeamMembersPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function TeamMemberAddRoute() {
  // Ember admitted any team-members viewer and rendered the form read-only for those without
  // edit permission; the page gates the fields + Send button on account/team_members/edit.
  return (
    <ProtectedRoute permission="account/team_members/view" redirectTo="/settings">
      <LazyPage>
        <TeamMemberAddPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function TeamMemberEditRoute() {
  // Ember admitted any team-members viewer and rendered the form read-only for those without
  // edit permission; the page gates the fields + Save button on account/team_members/edit.
  return (
    <ProtectedRoute permission="account/team_members/view" redirectTo="/settings">
      <LazyPage>
        <TeamMemberEditPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function TeamMemberInviteGuestRoute() {
  // Same access model as add/edit: any team-members viewer is admitted; the page gates the fields +
  // Send button on account/team_members/edit.
  return (
    <ProtectedRoute permission="account/team_members/view" redirectTo="/settings">
      <LazyPage>
        <TeamMemberInviteGuestPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function WorkspacesSettingsRoute() {
  return (
    <ProtectedRoute
      permission="account/workspaces/create"
      redirectTo="/settings"
      allowReadOnlyMasqAccess={true}
      errorMessage="You do not have permission to view this page. If you'd like access, let an Admin know."
    >
      <LazyPage>
        <WorkspacesSettingsPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function WorkspaceNewRoute() {
  // Ember admitted any account/view user and rendered the form read-only for those without
  // create permission; the page gates the form on account/workspaces/create.
  return (
    <ProtectedRoute permission="account/view" redirectTo="/settings" allowReadOnlyMasqAccess={true}>
      <LazyPage>
        <WorkspaceNewPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function NewRoleRoute() {
  return (
    <RolesGuard>
      <LazyPage>
        <NewRolePage />
      </LazyPage>
    </RolesGuard>
  );
}

export function RoleRoute() {
  return (
    <RolesGuard>
      <LazyPage>
        <RolePage />
      </LazyPage>
    </RolesGuard>
  );
}

export function AccountAiRoute() {
  return (
    <LazyPage>
      <AiSettingsPage />
    </LazyPage>
  );
}

export function AccountPrivacyRoute() {
  return (
    <LazyPage>
      <PrivacySettingsPage />
    </LazyPage>
  );
}

export function AccountSettingsRoute() {
  return (
    <ProtectedRoute permission="account/view" redirectTo="/settings" allowReadOnlyMasqAccess={true}>
      <LazyPage>
        <AccountSettingsPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

export function AccountSettingsEditRoute() {
  return (
    <ProtectedRoute permission="account/view" redirectTo="/settings" allowReadOnlyMasqAccess={true}>
      <LazyPage>
        <AccountSettingsEditPage />
      </LazyPage>
    </ProtectedRoute>
  );
}

// Layout for /settings/partner and its children (the list index and the create
// page). Gating lives here so it applies to every nested route, and the matched
// child renders through the Outlet.
export function AccountPartnerRoute() {
  // Gated on the account-level partner_account attribute (set in Flightdeck).
  const { account } = useAuth();
  if (account?.partner_account !== true) {
    return <NotFoundPage />;
  }
  return (
    <ProtectedRoute permission="account/edit" redirectTo="/settings">
      <Outlet />
    </ProtectedRoute>
  );
}

export function AccountPartnerIndexRoute() {
  return (
    <LazyPage>
      <AccountPartnerPage />
    </LazyPage>
  );
}

export function AccountPartnerCreateRoute() {
  return (
    <LazyPage>
      <AccountPartnerCreatePage />
    </LazyPage>
  );
}

export function WorkspaceAiBusinessProfileRoute() {
  return (
    <LastWorkspaceResolver>
      <LazyPage>
        <AIBusinessProfile />
      </LazyPage>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceAuditLogsRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="account/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
            .href
        }
        allowReadOnlyMasqAccess={true}
      >
        <LazyPage>
          <WorkspaceAuditLogsPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceApiKeysRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="account/api_credentials/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
            .href
        }
      >
        <LazyPage>
          <WorkspaceApiKeysPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceAutoArchiveRoute() {
  const router = useRouter();

  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.SegmentAutoArchive} fallback={NotFoundPage}>
      <LastWorkspaceResolver>
        <ProtectedRoute
          permission="journeys/workspace_settings/view"
          redirectTo={(params) =>
            router.buildLocation({
              to: '/workspaces/$workspaceId/settings',
              params: { workspaceId: params.workspaceId },
            }).href
          }
        >
          <LazyPage>
            <WorkspaceAutoArchivePage />
          </LazyPage>
        </ProtectedRoute>
      </LastWorkspaceResolver>
    </FeatureFlagGuard>
  );
}

export function WorkspaceEditRoute() {
  const router = useRouter();

  // Ember admitted workspace_settings/view users (and read-only masqueraders) and rendered the
  // form read-only for those without /edit; the page gates the form's readOnly on /edit. This
  // also matches the sibling settings routes (merge-options, auto-archive) that gate on /view.
  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/workspace_settings/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
            .href
        }
        allowReadOnlyMasqAccess={true}
      >
        <LazyPage>
          <WorkspaceEditPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceConfirmDeleteRoute() {
  const router = useRouter();

  // The only entry point is the workspaces-table Delete link, which gates solely on
  // `account/workspaces/create`. Gate the route on that same permission (and nothing more) so
  // access exactly matches the link — requiring the per-workspace `workspace_settings/view` too
  // would redirect a create-capable user who followed a working link. No
  // `allowReadOnlyMasqAccess`: delete has no read-only mode, and read-only masqueraders lack the
  // create permission anyway.
  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="account/workspaces/create"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
            .href
        }
      >
        <LazyPage>
          <WorkspaceConfirmDeletePage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function ExportsRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/exports/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/home', params: { workspaceId: params.workspaceId } })
            .href
        }
      >
        <LazyPage>
          <ExportsPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function LayoutsRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/layouts/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/home', params: { workspaceId: params.workspaceId } })
            .href
        }
      >
        <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
          <LazyPage>
            <LayoutsPage />
          </LazyPage>
        </PlanGuard>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceMergeOptionsRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/workspace_settings/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
            .href
        }
      >
        <LazyPage>
          <WorkspaceMergeOptionsPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceLanguageSettingsRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/language_settings/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
            .href
        }
      >
        <LazyPage>
          <WorkspaceLanguageSettingsPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceGeofencesSettingsRoute() {
  const router = useRouter();

  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.GeoFencingEnabled} fallback={NotFoundPage}>
      <LastWorkspaceResolver>
        <PlanFeatureGuard
          feature={PlanFeature.Geofences}
          redirectTo={(params) =>
            router.buildLocation({
              to: '/workspaces/$workspaceId/settings',
              params: { workspaceId: params.workspaceId },
            }).href
          }
        >
          <ProtectedRoute
            permission="journeys/workspace_settings/view"
            redirectTo={(params) =>
              router.buildLocation({
                to: '/workspaces/$workspaceId/settings',
                params: { workspaceId: params.workspaceId },
              }).href
            }
          >
            <LazyPage>
              <WorkspaceGeofencesSettingsPage />
            </LazyPage>
          </ProtectedRoute>
        </PlanFeatureGuard>
      </LastWorkspaceResolver>
    </FeatureFlagGuard>
  );
}

export function WorkspaceSuppressionsRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/workspace_settings/view"
        redirectTo={(params) =>
          router.buildLocation({ to: '/workspaces/$workspaceId/settings', params: { workspaceId: params.workspaceId } })
            .href
        }
      >
        <LazyPage>
          <WorkspaceSuppressionsPage />
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

export function WorkspaceCustomSmsLayout() {
  const router = useRouter();
  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.CustomSms} fallback={NotFoundPage}>
      <LastWorkspaceResolver>
        <ProtectedRoute
          permission="journeys/workspace_settings/view"
          redirectTo={(params) =>
            router.buildLocation({
              to: '/workspaces/$workspaceId/settings',
              params: { workspaceId: params.workspaceId },
            }).href
          }
        >
          <Outlet />
        </ProtectedRoute>
      </LastWorkspaceResolver>
    </FeatureFlagGuard>
  );
}

export function WorkspaceCustomSmsSettingsRoute() {
  return (
    <LazyPage>
      <WorkspaceCustomSmsSettingsPage />
    </LazyPage>
  );
}

export function WorkspaceCustomSmsCredentialDetailRoute() {
  return (
    <LazyPage>
      <WorkspaceCustomSmsCredentialDetailPage />
    </LazyPage>
  );
}

export function HarSanitizerRoute() {
  return (
    <LazyPage>
      <HarSanitizerPage />
    </LazyPage>
  );
}

export function NoAccessibleWorkspacesRoute() {
  return (
    <LazyPage>
      <NoAccessibleWorkspacesPage />
    </LazyPage>
  );
}

// Guest-invitation accept page (standalone, chrome-less, reached from an email link).
export function AcceptGuestInvitationRoute() {
  return (
    <LazyPage>
      <AcceptGuestInvitationPage />
    </LazyPage>
  );
}

export function PostCancelRoute() {
  return (
    <LazyPage>
      <PostCancelPage />
    </LazyPage>
  );
}

export function DeliverabilityRoute() {
  return (
    <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
      <LastWorkspaceResolver>
        <LazyPage>
          <DeliverabilityPage />
        </LazyPage>
      </LastWorkspaceResolver>
    </PlanGuard>
  );
}

export function EmailClientsRoute() {
  return (
    <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
      <LastWorkspaceResolver>
        <LazyPage>
          <EmailClientsPage />
        </LazyPage>
      </LastWorkspaceResolver>
    </PlanGuard>
  );
}

// The Ember `layout` (detail) route nests under the `layouts` route, whose
// `beforeModel` redirected builder-plan accounts away — mirrored with
// PlanGuard. It had no permission guard of its own (permissions only change
// what the page renders).
export function LayoutDetailRoute() {
  return (
    <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
      <LastWorkspaceResolver>
        <LazyPage>
          <LayoutDetailPage />
        </LazyPage>
      </LastWorkspaceResolver>
    </PlanGuard>
  );
}

// Like the detail route above, the Ember `layout.edit` and `layouts.new`
// routes nest under the builder-plan-gated `layouts` route and had no
// permission guard of their own.
export function LayoutEditRoute() {
  return (
    <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
      <LastWorkspaceResolver>
        <LazyPage>
          <LayoutEditPage />
        </LazyPage>
      </LastWorkspaceResolver>
    </PlanGuard>
  );
}

export function NewLayoutRoute() {
  return (
    <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
      <LastWorkspaceResolver>
        <LazyPage>
          <NewLayoutPage />
        </LazyPage>
      </LastWorkspaceResolver>
    </PlanGuard>
  );
}

// Mirrors Ember's `layouts.starters` route guards: the parent `layouts` route
// redirected builder-plan accounts away, and the starters route itself
// required `journeys/layouts/create` (falling back to the layouts list).
export function LayoutsStartersRoute() {
  const router = useRouter();

  return (
    <LastWorkspaceResolver>
      {/* PlanGuard sits outside ProtectedRoute to match Ember's guard order:
          the parent `layouts` route redirected builder-plan accounts (to the
          available-plans page) before the starters route's permission check
          ever ran. */}
      <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
        <ProtectedRoute
          permission="journeys/layouts/create"
          redirectTo={(params) =>
            router.buildLocation({
              to: '/workspaces/$workspaceId/journeys/layouts',
              params: { workspaceId: params.workspaceId ?? 'last' },
            }).href
          }
        >
          <LazyPage>
            <LayoutStartersPage />
          </LazyPage>
        </ProtectedRoute>
      </PlanGuard>
    </LastWorkspaceResolver>
  );
}

// Builder-plan accounts are redirected away, matching the Ember route's
// `beforeModel` redirect to the available-plans page.
export function MessageLibraryRoute() {
  return (
    <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
      <LastWorkspaceResolver>
        <LazyPage>
          <MessageLibraryPage />
        </LazyPage>
      </LastWorkspaceResolver>
    </PlanGuard>
  );
}

type ChildRouteProps = {
  children: ReactNode;
};

type GeofencesRouteProps = ChildRouteProps & {
  permission: Permission;
  redirectTo: (params: Record<string, string | undefined>) => string;
  /**
   * Require a real geoset in the URL before rendering. Set on the geoset-scoped
   * geofence pages (`/geosets/:geosetId/geofences/…`); left off for the geosets
   * index, which has no geoset id. See {@link GeosetRequiredGuard}.
   */
  requireGeoset?: boolean;
};

export function AiRoute({ children }: ChildRouteProps) {
  return (
    <AiEnabledGuard fallback={AiNotEnabledFallback}>
      <LastWorkspaceResolver>{children}</LastWorkspaceResolver>
    </AiEnabledGuard>
  );
}

export function AiSkillsRoute({ children }: ChildRouteProps) {
  return (
    <AiEnabledGuard fallback={AiNotEnabledFallback}>
      <LastWorkspaceResolver>{children}</LastWorkspaceResolver>
    </AiEnabledGuard>
  );
}

export function GeofencesRoute({ children, permission, redirectTo, requireGeoset = false }: GeofencesRouteProps) {
  const router = useRouter();

  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.GeoFencingEnabled} fallback={NotFoundPage}>
      <LastWorkspaceResolver>
        <PlanFeatureGuard
          feature={PlanFeature.Geofences}
          redirectTo={(params) =>
            router.buildLocation({ to: '/workspaces/$workspaceId/home', params: { workspaceId: params.workspaceId } })
              .href
          }
        >
          <ProtectedRoute permission={permission} redirectTo={redirectTo}>
            <LazyPage>{requireGeoset ? <GeosetRequiredGuard>{children}</GeosetRequiredGuard> : children}</LazyPage>
          </ProtectedRoute>
        </PlanFeatureGuard>
      </LastWorkspaceResolver>
    </FeatureFlagGuard>
  );
}

// Design Studio dashboard (CON-7495), natively routed behind the
// `ds-dashboard-in-hydra` kill-switch. Flag off: the embedded Design Studio
// bundle serves this URL — render the same embedded-app host the
// design-studio layout route renders, so the rendered app always agrees with
// route ownership (`route-ownership.ts` falls back to `embedded` on the same
// flag, which keeps the embedded-app history bridge treating the URL as an
// in-bundle navigation). Flag on: ownership answers `hydra`, in-bundle
// navigations to this URL go through a full TanStack load, and the native
// page renders inside the authenticated layout.
export function DesignStudioDashboardRoute() {
  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.DsDashboardInHydra} fallback={DesignStudioRoute}>
      <LastWorkspaceResolver>
        <LazyPage>
          <DesignStudioDashboardPage />
        </LazyPage>
      </LastWorkspaceResolver>
    </FeatureFlagGuard>
  );
}

// Design Studio global styles (CON-7588), natively routed behind the
// `ds-global-styles-in-hydra` kill-switch, mirroring the dashboard route above.
// Flag off: the embedded Design Studio bundle serves this URL — render the same
// embedded-app host the design-studio layout route renders, so the rendered app
// always agrees with route ownership (`route-ownership.ts` falls back to
// `embedded` on the same flag, which keeps the embedded-app history bridge
// treating the URL as an in-bundle navigation). Flag on: ownership answers
// `hydra`, in-bundle navigations to this URL go through a full TanStack load,
// and the native page renders inside the authenticated layout.
export function DesignStudioGlobalStylesRoute() {
  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.DsGlobalStylesInHydra} fallback={DesignStudioRoute}>
      <LastWorkspaceResolver>
        <LazyPage>
          <DesignStudioGlobalStylesPage />
        </LazyPage>
      </LastWorkspaceResolver>
    </FeatureFlagGuard>
  );
}

export function AssetsRoute() {
  return (
    <LastWorkspaceResolver>
      <LazyPage>
        <AssetsPage />
      </LazyPage>
    </LastWorkspaceResolver>
  );
}

export function SnippetsLayoutRoute() {
  return (
    <LastWorkspaceResolver>
      <LazyPage>
        <SnippetsLayout />
      </LazyPage>
    </LastWorkspaceResolver>
  );
}
